import React from 'react';

/**
 * Tiny bouncing Shippy box — for inline use inside buttons.
 * Props:
 *  - size      : number (px) — default 16
 *  - className : extra classes
 */
export function ShippySpinner({ size = 16, className = '' }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 32 32"
      width={size}
      height={size}
      className={className}
    >
      <g>
        <animateTransform attributeName="transform" type="translate" values="0,0;0,-2;0,0" dur="0.4s" repeatCount="indefinite" />
        {/* Box top */}
        <path d="M6 10 L16 5 L26 10 L16 15 Z" fill="#93C5FD" stroke="#1E40AF" strokeWidth="0.8" />
        {/* Box front */}
        <path d="M6 10 L16 15 L16 25 L6 20 Z" fill="#3B82F6" stroke="#1E40AF" strokeWidth="0.8" />
        {/* Box side */}
        <path d="M26 10 L16 15 L16 25 L26 20 Z" fill="#60A5FA" stroke="#1E40AF" strokeWidth="0.8" />
        {/* Tape */}
        <line x1="11" y1="12.5" x2="11" y2="22" stroke="#FCD34D" strokeWidth="1" strokeLinecap="round" opacity="0.8" />
        {/* Eyes */}
        <circle cx="10" cy="16" r="1.2" fill="white" />
        <circle cx="14" cy="15.5" r="1.2" fill="white" />
        <circle cx="10.4" cy="15.8" r="0.6" fill="#1E293B" />
        <circle cx="14.4" cy="15.3" r="0.6" fill="#1E293B" />
        {/* Smile */}
        <path d="M9 18.5 Q11.5 20.5, 15 18.5" fill="none" stroke="#1E293B" strokeWidth="0.8" strokeLinecap="round" />
      </g>
      {/* Little legs */}
      <rect x="9" y="24" width="2.5" height="4" rx="1.2" fill="#1E40AF">
        <animateTransform attributeName="transform" type="rotate" values="-6,10.25,24;6,10.25,24;-6,10.25,24" dur="0.4s" repeatCount="indefinite" />
      </rect>
      <rect x="17" y="24" width="2.5" height="4" rx="1.2" fill="#1E40AF">
        <animateTransform attributeName="transform" type="rotate" values="6,18.25,24;-6,18.25,24;6,18.25,24" dur="0.4s" repeatCount="indefinite" />
      </rect>
    </svg>
  );
}

/**
 * Dancing Shippy mascot loader — replaces generic spinners.
 * Uses pure CSS keyframes so no extra dependencies are needed.
 *
 * Props:
 *  - size   : number (px) — default 64
 *  - message: optional text below the mascot
 */
export default function ShippyLoader({ size = 64, message }) {
  return (
    <div className="flex flex-col items-center gap-2">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 100 100"
        width={size}
        height={size}
        className="shippy-dance"
      >
        <defs>
          <linearGradient id="sl-top" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#93C5FD" />
            <stop offset="100%" stopColor="#60A5FA" />
          </linearGradient>
          <linearGradient id="sl-front" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#3B82F6" />
            <stop offset="100%" stopColor="#2563EB" />
          </linearGradient>
          <linearGradient id="sl-side" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#60A5FA" />
            <stop offset="100%" stopColor="#3B82F6" />
          </linearGradient>
        </defs>

        {/* Left leg */}
        <rect x="30" y="72" width="8" height="16" rx="4" fill="#1E40AF">
          <animateTransform attributeName="transform" type="rotate" values="-8,34,72;8,34,72;-8,34,72" dur="0.5s" repeatCount="indefinite" />
        </rect>
        {/* Left shoe */}
        <ellipse cx="34" cy="90" rx="8" ry="4" fill="#1D4ED8">
          <animateTransform attributeName="transform" type="rotate" values="-8,34,72;8,34,72;-8,34,72" dur="0.5s" repeatCount="indefinite" />
        </ellipse>

        {/* Right leg */}
        <rect x="54" y="72" width="8" height="16" rx="4" fill="#1E40AF">
          <animateTransform attributeName="transform" type="rotate" values="8,58,72;-8,58,72;8,58,72" dur="0.5s" repeatCount="indefinite" />
        </rect>
        {/* Right shoe */}
        <ellipse cx="58" cy="90" rx="8" ry="4" fill="#1D4ED8">
          <animateTransform attributeName="transform" type="rotate" values="8,58,72;-8,58,72;8,58,72" dur="0.5s" repeatCount="indefinite" />
        </ellipse>

        {/* Body group — bounces */}
        <g>
          <animateTransform attributeName="transform" type="translate" values="0,0;0,-4;0,0" dur="0.5s" repeatCount="indefinite" />

          {/* Left arm waving */}
          <g>
            <animateTransform attributeName="transform" type="rotate" values="0,20,48;-20,20,48;0,20,48" dur="0.6s" repeatCount="indefinite" />
            <path d="M20 48 Q10 38, 8 26 Q7 20, 12 18" fill="none" stroke="#1E40AF" strokeWidth="7" strokeLinecap="round" />
            <circle cx="12" cy="17" r="5" fill="#FCD34D" />
          </g>

          {/* Right arm */}
          <g>
            <animateTransform attributeName="transform" type="rotate" values="0,72,48;15,72,48;0,72,48" dur="0.6s" repeatCount="indefinite" />
            <path d="M72 48 Q82 52, 80 62" fill="none" stroke="#1E40AF" strokeWidth="7" strokeLinecap="round" />
            <circle cx="80" cy="63" r="5" fill="#FCD34D" />
          </g>

          {/* Box body */}
          <path d="M22 30 L46 18 L76 30 L52 42 Z" fill="url(#sl-top)" stroke="#1E40AF" strokeWidth="1.5" />
          <line x1="46" y1="18" x2="52" y2="42" stroke="#FCD34D" strokeWidth="3" strokeLinecap="round" opacity="0.8" />
          <path d="M22 30 L52 42 L52 75 L22 63 Z" fill="url(#sl-front)" stroke="#1E40AF" strokeWidth="1.5" />
          <path d="M76 30 L52 42 L52 75 L76 63 Z" fill="url(#sl-side)" stroke="#1E40AF" strokeWidth="1.5" />
          <line x1="37" y1="36" x2="37" y2="69" stroke="#FCD34D" strokeWidth="3" strokeLinecap="round" opacity="0.8" />

          {/* Eyes */}
          <ellipse cx="33" cy="48" rx="3.5" ry="4" fill="white" />
          <ellipse cx="45" cy="46" rx="3.5" ry="4" fill="white" />
          <ellipse cx="34.5" cy="47.5" rx="2" ry="2.5" fill="#1E293B" />
          <ellipse cx="46.5" cy="45.5" rx="2" ry="2.5" fill="#1E293B" />
          <circle cx="35.5" cy="46" r="1" fill="white" />
          <circle cx="47.5" cy="44" r="1" fill="white" />

          {/* Smile */}
          <path d="M30 54 Q38 62, 48 54" fill="none" stroke="#1E293B" strokeWidth="2.5" strokeLinecap="round" />

          {/* Cheeks */}
          <ellipse cx="28" cy="55" rx="3" ry="2" fill="#F9A8D4" opacity="0.5" />
          <ellipse cx="50" cy="53" rx="3" ry="2" fill="#F9A8D4" opacity="0.5" />
        </g>
      </svg>

      {message && (
        <span className="text-sm text-muted-foreground animate-pulse">{message}</span>
      )}
    </div>
  );
}
